import type { FileRecord } from '@reados/contracts';
import type { LightboxImage } from './ImageLightbox';
import { MediaTicketCache, type MediaReference } from './media';
import { resolveReference, type ResolvedReference } from './references';

/** Only owned resource identities or raster bytes enter the parent document. */
export class ImageSourceResolver {
  private issued = new Map<string, ResolvedReference>();
  constructor(private media: MediaTicketCache, private origin: string,
    private authorize: (reference: ResolvedReference | null, source: string) => ResolvedReference | null = reference => reference) {}

  remember(reference: ResolvedReference, resolved: MediaReference) {
    if (reference.kind !== 'image') return;
    this.issued.set(resolved.resourceUrl, reference);
    while (this.issued.size > 256) this.issued.delete(this.issued.keys().next().value!);
  }

  async resolve(input: { source: string; title?: string }, files: FileRecord[], refresh = false, documentPath?: string): Promise<LightboxImage> {
    const fallback = { id: input.source, source: '', title: input.title };
    if (input.source.length <= 1_000_000 && /^data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/=\s]+$/.test(input.source))
      return { ...fallback, source: input.source };
    const reference = this.authorize(this.issued.get(input.source) ?? resolveReference(input.source, files, this.origin, documentPath), input.source);
    if (!reference || reference.kind !== 'image') return fallback;
    const image = { ...fallback, id: reference.sourceIdentity || reference.target.path || input.source, title: input.title || reference.label };
    try {
      const resolved = await this.media.resolve(reference, refresh);
      this.remember(reference, resolved);
      return { ...image, source: resolved.resourceUrl };
    } catch { return image; }
  }
}
